import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HOUSE_ABANDON_DAYS } from '../../domain/house';
import { format_num } from '../../utils/format/format_num';
import { useNotifyPermission, type NotifyReward, type NotifySource } from './useNotifyPermission';
import './NotifyPermissionPrompt.css';

const SHELL_ICON = '/ui/shell-icon.png';

interface NotifyPermissionPromptProps {
  source: NotifySource;
  /** Pearls promised on the Allow button (the server pays the real amount). */
  rewardPearls: number;
  /** Onboarding: the reward doubles — the Allow button says "x2". */
  doubles?: boolean;
  /** Called the moment the server confirmed delivery (the reward is credited). */
  onGranted?: (reward: NotifyReward) => void;
  /** The player is done here: allowed, or chose not to. */
  onDone: (granted: boolean) => void;
}

/**
 * "Let the bot message you" — a section at the foot of the dialog it sits in,
 * asked where losing touch costs the player most: at the end of the tutorial and
 * right after buying a house (houses are lost after HOUSE_ABANDON_DAYS away, and
 * most players never open the bot). A granted permission just closes it: the
 * host shows the reward, and the bot's thank-you arrives in the chat.
 */
export default function NotifyPermissionPrompt({ source, rewardPearls, doubles = false, onGranted, onDone }: NotifyPermissionPromptProps) {
  const { t } = useTranslation();
  const { phase, allow, decline, retry, giveUp } = useNotifyPermission(source, onGranted);

  useEffect(() => {
    if (phase === 'granted') onDone(true);
  }, [phase, onDone]);

  if (phase === 'granted') return null;

  const allowButton = (
    <button type="button" className="notify-prompt__allow" onClick={allow}>
      {t("Allow")}
      <span className="notify-prompt__chip">
        {doubles ? 'x2' : `+${format_num(rewardPearls)}`}
        {!doubles && <img src={SHELL_ICON} alt="" width={14} height={12} />}
      </span>
    </button>
  );

  if (phase === 'verifying') {
    return (
      <div className="notify-prompt" role="status" aria-live="polite">
        <div className="notify-prompt__spinner" aria-hidden="true" />
        <p className="notify-prompt__text">{t("Tap Allow in the Telegram window")}</p>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div className="notify-prompt">
        <p className="notify-prompt__title">{t("Messages are still off")}</p>
        <button type="button" className="notify-prompt__allow" onClick={retry}>{t("Try again")}</button>
        <button type="button" className="notify-prompt__no" onClick={() => { giveUp('skipped'); onDone(false); }}>{t("Skip")}</button>
      </div>
    );
  }

  if (phase === 'confirmDecline') {
    return (
      <div className="notify-prompt">
        <p className="notify-prompt__title">{t("Are you sure?")}</p>
        <p className="notify-prompt__text">{t("Without reminders your house may be lost while you're away.")}</p>
        {allowButton}
        <button type="button" className="notify-prompt__no" onClick={() => { giveUp('declined'); onDone(false); }}>{t("Yes, no reminders")}</button>
      </div>
    );
  }

  return (
    <div className="notify-prompt">
      <p className="notify-prompt__title">
        <span className="notify-prompt__bell" aria-hidden="true">🔔</span>
        {t("We'll miss you if you leave!")}
      </p>
      <p className="notify-prompt__text">
        {source === 'onboarding'
          ? t("Let our bot remind you — and double your reward.")
          : t("A house is lost after {{days}} days away. Let our bot remind you.", { days: HOUSE_ABANDON_DAYS })}
      </p>
      {allowButton}
      <button type="button" className="notify-prompt__no" onClick={decline}>{t("Not now")}</button>
    </div>
  );
}
